import React from "react";
import { useTheme } from "../../../../contexts/ThemeContext.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faFileAlt, faGlobe, faImage } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";

export default function TrustPageSection() {
  const { isDark } = useTheme();

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">
        <h2 className="text-xl font-semibold mb-4 text-foreground">Trust Page</h2>
        <p className="text-sm text-muted-foreground mb-6">Configure your public trust page to showcase your security and compliance posture.</p>
        
        {/* Trust Page Settings */}
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="trustPageEnabledInput" className="block text-sm font-medium text-foreground">
                Trust Page Enabled
              </label>
              <select
                id="trustPageEnabledInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="false">Disabled</option>
                <option value="true">Enabled</option>
              </select>
              <p className="text-xs text-muted-foreground">
                Enable or disable your public trust page.
              </p>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="trustPageURLInput" className="block text-sm font-medium text-foreground">
                Trust Page URL
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 border border-r-0 border-border rounded-l-lg bg-muted text-muted-foreground">
                  https://trust.deiterate.com/
                </span>
                <input
                  type="text"
                  id="trustPageURLInput"
                  className="flex-1 px-3 py-2 border border-border rounded-r-lg bg-background text-foreground"
                  placeholder="your-company"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Set your custom trust page URL.
              </p>
            </div>
          </div>
          
          {/* Trust Page Content */}
          <div className="border-t border-border pt-6 mt-6">
            <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faFileAlt} className="text-primary" />
              Trust Page Content
            </h3>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="companyNameInput" className="block text-sm font-medium text-foreground">
                  Company Name
                </label>
                <input
                  type="text"
                  id="companyNameInput"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  placeholder="Enter company name"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="companyDescriptionInput" className="block text-sm font-medium text-foreground">
                  Company Description
                </label>
                <textarea
                  id="companyDescriptionInput"
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground resize-none"
                  placeholder="Enter a brief description of your company"
                ></textarea>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="companyLogoInput" className="block text-sm font-medium text-foreground">
                  Company Logo
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faImage} className="text-muted-foreground text-xl" />
                  </div>
                  <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">
                    Upload Logo
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Upload your company logo (recommended size: 200x200px, PNG or SVG format).
                </p>
              </div>
            </div>
          </div>
          
          {/* Compliance Badges */}
          <div className="border-t border-border pt-6 mt-6">
            <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faShieldAlt} className="text-primary" />
              Compliance Badges
            </h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border border-border rounded-lg bg-background">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="bg-blue-500/10 p-2 rounded-full">
                        <FontAwesomeIcon icon={faShieldAlt} className="text-blue-600" />
                      </div>
                      <span className="font-medium text-foreground">ISO 27001</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Display ISO 27001 certification badge on your trust page.
                  </p>
                </div>
                
                <div className="p-4 border border-border rounded-lg bg-background">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="bg-purple-500/10 p-2 rounded-full">
                        <FontAwesomeIcon icon={faShieldAlt} className="text-purple-600" />
                      </div>
                      <span className="font-medium text-foreground">ISO 27701</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Display ISO 27701 certification badge on your trust page.
                  </p>
                </div>
                
                <div className="p-4 border border-border rounded-lg bg-background">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="bg-green-500/10 p-2 rounded-full">
                        <FontAwesomeIcon icon={faShieldAlt} className="text-green-600" />
                      </div>
                      <span className="font-medium text-foreground">SOC 2</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Display SOC 2 certification badge on your trust page.
                  </p>
                </div>
                
                <div className="p-4 border border-border rounded-lg bg-background">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="bg-amber-500/10 p-2 rounded-full">
                        <FontAwesomeIcon icon={faGlobe} className="text-amber-600" />
                      </div>
                      <span className="font-medium text-foreground">GDPR</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Display GDPR compliance badge on your trust page.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center mt-8">
            <button className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center gap-2">
              <FontAwesomeIcon icon={faGlobe} />
              Save Trust Page Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
